<template>
  <div class="bg-white dark:bg-[#1C2128] rounded-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
    <!-- Card header + tabs -->
    <div class="px-5 py-3.5 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
      <div>
        <p class="text-sm font-semibold text-gray-900 dark:text-white">Device Breakdown</p>
        <p class="text-xs text-gray-400 dark:text-gray-500 mt-0.5">Platform, OS and browser split</p>
      </div>
      <div class="flex items-center gap-1 bg-gray-100 dark:bg-gray-800 rounded-lg p-1">
        <button v-for="t in TABS" :key="t.key" @click="activeTab = t.key"
          :class="activeTab === t.key
            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white shadow-sm'
            : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
          class="px-3 py-1.5 text-xs font-semibold rounded-md transition-all">
          {{ t.label }}
        </button>
      </div>
    </div>

    <!-- Skeleton -->
    <div v-if="loading && !deviceData" class="animate-pulse p-5 flex gap-6">
      <div class="w-36 h-36 bg-gray-100 dark:bg-gray-800 rounded-full flex-shrink-0"></div>
      <div class="flex-1 space-y-3 pt-2">
        <div v-for="i in 3" :key="i" class="flex items-center gap-3">
          <div class="w-3 h-3 rounded-full bg-gray-200 dark:bg-gray-700"></div>
          <div class="h-3 bg-gray-200 dark:bg-gray-700 rounded flex-1"></div>
          <div class="h-3 bg-gray-100 dark:bg-gray-800 rounded w-8"></div>
        </div>
      </div>
    </div>

    <!-- Empty -->
    <div v-else-if="isEmpty" class="flex flex-col items-center justify-center py-12 px-4 text-center">
      <p class="text-sm text-gray-400 dark:text-gray-500">No device data for this period.</p>
    </div>

    <!-- Content -->
    <div v-else class="p-5 flex flex-col sm:flex-row gap-6 items-center">
      <div ref="chartContainer" class="w-40 h-40 flex-shrink-0"></div>
      <div class="flex-1 min-w-0 space-y-2">
        <div v-for="item in legendItems" :key="item.name"
          class="flex items-center gap-2.5">
          <span class="w-2.5 h-2.5 rounded-full flex-shrink-0" :style="{ backgroundColor: item.color }"></span>
          <span class="text-sm text-gray-700 dark:text-gray-200 truncate flex-1">{{ item.name }}</span>
          <span class="text-xs font-semibold text-gray-500 dark:text-gray-400 tabular-nums flex-shrink-0">{{ item.pct }}%</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue'
import { useAnalyticsTrafficStore } from 'stores/analyticsTraffic.js'

const props = defineProps({ duration: { type: String, required: true } })
const store = useAnalyticsTrafficStore()

const TABS = [
  { key: 'platform', label: 'Platform' },
  { key: 'os',       label: 'OS'       },
  { key: 'browser',  label: 'Browser'  },
]
const PALETTE = ['#2563eb', '#10b981', '#8b5cf6', '#f59e0b', '#ef4444', '#06b6d4', '#ec4899']

const activeTab     = ref('platform')
const chartContainer = ref(null)

const loading    = computed(() => store.deviceLoading)
const deviceData = computed(() => store.deviceData[props.duration] ?? null)

const currentSeries = computed(() => {
  const d = deviceData.value
  if (!d) return []
  const key = activeTab.value
  return (d[key]?.[0]?.data ?? []).slice().sort((a, b) => b.y - a.y)
})

const isEmpty = computed(() => deviceData.value && currentSeries.value.length === 0)

const total = computed(() => currentSeries.value.reduce((s, d) => s + d.y, 0))
const legendItems = computed(() =>
  currentSeries.value.map((d, i) => ({
    name:  d.name,
    pct:   total.value ? ((d.y / total.value) * 100).toFixed(1) : '0',
    color: PALETTE[i % PALETTE.length],
  }))
)

let chart = null

function renderChart() {
  if (!chartContainer.value || !currentSeries.value.length) return
  if (chart) { chart.destroy(); chart = null }
  chart = Highcharts.chart(chartContainer.value, {
    chart: { type: 'pie', backgroundColor: 'transparent', margin: [0, 0, 0, 0], spacing: [0, 0, 0, 0] },
    title: { text: null },
    credits: { enabled: false },
    legend: { enabled: false },
    tooltip: {
      backgroundColor: '#1f2937',
      borderColor: '#374151',
      borderRadius: 8,
      style: { color: '#f9fafb', fontSize: '12px' },
      pointFormat: '<b>{point.name}</b>: {point.percentage:.1f}%',
    },
    plotOptions: {
      pie: {
        innerSize: '60%',
        dataLabels: { enabled: false },
        colors: PALETTE,
      },
    },
    series: [{
      name: TABS.find(t => t.key === activeTab.value)?.label ?? '',
      colorByPoint: true,
      data: currentSeries.value.map(d => ({ name: d.name, y: d.y })),
    }],
  })
}

watch(currentSeries, async () => {
  await nextTick()
  renderChart()
})

onMounted(() => {
  if (currentSeries.value.length) renderChart()
})

onUnmounted(() => {
  if (chart) { chart.destroy(); chart = null }
})
</script>
